/* eslint-disable no-use-before-define */
import { DiContainer, Injectable, InjectProperty } from '@jack-henry/frontend-utils/di';
import { delay } from '@jack-henry/frontend-utils/functions';
import { ObservationSource } from '@jack-henry/frontend-utils/observable';
import { ConfigurationService } from '@treasury/core/config'; // Import the ConfigurationService from the appropriate module
import { LoggingService } from '@treasury/core/logging';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import {
    MitekAutoCaptureHints,
    MitekCaptureResult,
    MitekFeedback,
    MitekSdk,
    MitekSdkError,
} from './mitek-capture-types';
// @ts-expect-error This import really does work despite the TS module resolution error
import * as mitekScienceSDKImport from './mitek/mitek-science-sdk.js';
const mitekScienceSDK = mitekScienceSDKImport as unknown as MitekSdk;

@Injectable()
export class CheckCaptureService {
    @InjectProperty({ async: true, token: FeatureFlagService })
    declare private readonly ffServicePromise: Promise<FeatureFlagService>;

    @InjectProperty()
    declare private readonly config: ConfigurationService;

    @InjectProperty()
    declare private readonly logService: LoggingService;

    private readonly displayStartedStream = new ObservationSource<
        MitekCaptureResult['response']['docType']
    >();

    private readonly displayStoppedStream = new ObservationSource<void>();

    private readonly processingStartedStream = new ObservationSource<boolean>();

    private readonly captureCompletedStream = new ObservationSource<MitekCaptureResult>();

    private readonly sdkErrorStream = new ObservationSource<MitekSdkError>();

    private readonly frameProcessingFeedbackStream = new ObservationSource<MitekFeedback>();

    public readonly displayStarted$ = this.displayStartedStream.toObservable();

    public readonly displayStopped$ = this.displayStoppedStream.toObservable();

    public readonly processingStarted$ = this.processingStartedStream.toObservable();

    public readonly captureCompleted$ = this.captureCompletedStream.toObservable();

    public readonly sdkError$ = this.sdkErrorStream.toObservable();

    public readonly frameProcessingFeedback$ = this.frameProcessingFeedbackStream.toObservable();

    private mitekSDKPath = '/pwa/mitek/mitekSDK';

    private lastMitekContainerExists = false;

    private mitekContainerObserver: MutationObserver = new MutationObserver(() => {
        const mitekContainerExists = document.body.contains(
            document.getElementById('mitekDisplayContainer')
        );
        const justRemoved = this.lastMitekContainerExists && !mitekContainerExists;
        if (justRemoved) {
            this.displayStoppedStream.emit();
        }
        this.lastMitekContainerExists = !!mitekContainerExists;
    });

    private license?: string;

    private captureStartTime?: number;

    private readonly hintLoggingDelayMs = 10000; // 10 seconds delay before logging hints

    private lastLoggedHintKey?: string;

    static getInstance() {
        return getServiceInstance();
    }

    private connectSdkErrorListener() {
        mitekScienceSDK.on('SDK_ERROR', (err: any) => {
            this.logService.logError(
                `check-capture-service Mitek SDK error:\n${JSON.stringify(err, undefined, 2)}`,
                err,
                { capture_step: 'Capture' }
            );
            this.sdkErrorStream.emit(err);
        });
    }

    public async init() {
        this.logService.logInfo('Initializing CheckCaptureService');
        this.license = this.config.remoteDepositToken;
        this.connectSdkErrorListener();

        const ffService = await this.ffServicePromise;
        if (await ffService.isEnabled(Feature.PwaMitekPreload)) {
            this.logService.logInfo('Preloading Mitek SDK');
            mitekScienceSDK.cmd('COMPONENT_PRELOAD', {
                preloadComponents: ['DOCUMENTS'], // what components to preload for quick UX. 'ALL' / 'SELFIE', 'BARCODE', 'DOCUMENTS'. Note: DOCUMENTS also includes checks and generic documents.
                mitekSDKPath: this.mitekSDKPath,
                options: {
                    license: this.license,
                },
            });
        }
    }

    public attachListeners(documentType: MitekCaptureResult['response']['docType']) {
        this.connectSdkErrorListener();

        mitekScienceSDK.on('CAMERA_DISPLAY_STARTED', (result: any) => {
            this.logService.logInfo('CheckCaptureService - CAMERA_DISPLAY_STARTED', { result });
            this.displayStartedStream.emit(documentType);
        });

        mitekScienceSDK.on('FRAME_PROCESSING_STARTED', () => {
            this.logService.logInfo('CheckCaptureService - FRAME_PROCESSING_STARTED');
            this.mitekContainerObserver.observe(document.body, {
                childList: true,
                subtree: true,
            });
            this.processingStartedStream.emit(true);
        });

        mitekScienceSDK.on('FRAME_CAPTURE_RESULT', (result: MitekCaptureResult) => {
            const { status, mode, docType } = result.response;
            this.logService.logInfo('CheckCaptureService - FRAME_CAPTURE_RESULT', {
                status,
                mode,
                docType,
            });
            this.captureStartTime = undefined; // Reset capture timing when capture completes
            this.lastLoggedHintKey = undefined; // Reset last logged hint when capture completes
            this.captureCompletedStream.emit(result);
        });

        mitekScienceSDK.on('FRAME_PROCESSING_FEEDBACK', (result: MitekFeedback) => {
            this.frameProcessingFeedbackStream.emit(result);
            if (result.key in MitekAutoCaptureHints) {
                const currentTime = Date.now();
                const captureSessionDuration = this.captureStartTime
                    ? currentTime - this.captureStartTime
                    : 0;

                // Only log hints if the capture session has been running for longer than the delay threshold
                // and if this is a different hint than the last one we logged
                if (
                    captureSessionDuration >= this.hintLoggingDelayMs &&
                    result.key !== this.lastLoggedHintKey
                ) {
                    this.logService.logInfo(
                        `Mitek Capture Hint: ${MitekAutoCaptureHints[result.key]}`,
                        {
                            sessionDurationMs: captureSessionDuration,
                            hint: result.key,
                        }
                    );
                    this.lastLoggedHintKey = result.key;
                }

                this.showHint(MitekAutoCaptureHints[result.key]);
            }
        });
    }

    public removeListeners() {
        mitekScienceSDK.cmd('SDK_REMOVE_LISTENERS');
    }

    public async startCapture(
        documentType: MitekCaptureResult['response']['docType'],
        mode: 'AUTO_CAPTURE' | 'MANUAL_CAPTURE' = 'AUTO_CAPTURE'
    ) {
        this.logService.logInfo('CheckCaptureService - startCapture', { documentType, mode });
        this.captureStartTime = Date.now(); // Record when capture session starts
        this.lastLoggedHintKey = undefined; // Reset last logged hint for new capture session
        this.removeListeners();
        this.attachListeners(documentType);
        try {
            await mitekScienceSDK.cmd('CAPTURE_AND_PROCESS_FRAME', {
                mode,
                documentType,
                mitekSDKPath: this.mitekSDKPath,
                options: {
                    qualityPercent: 90,
                    license: this.license,
                },
            });
        } catch (error) {
            this.logService.logError(
                'CheckCaptureService - Error during CAPTURE_AND_PROCESS_FRAME',
                error as unknown as Error
            );
            throw new Error('Error during CAPTURE_AND_PROCESS_FRAME');
        }
    }

    public showHint(hint: string, hintDuration = 2400) {
        mitekScienceSDK.cmd('SHOW_HINT', {
            options: {
                hintText: hint,
                hintDuration,
            },
        });
    }

    public async stopCapture(mode: MitekCaptureResult['response']['mode'] = 'AUTO') {
        this.logService.logInfo('CheckCaptureService - stopCapture');
        this.captureStartTime = undefined; // Reset capture timing when stopping
        this.lastLoggedHintKey = undefined; // Reset last logged hint when stopping

        try {
            mitekScienceSDK.cmd('SDK_STOP');
            this.removeListeners();
        } catch (err: unknown) {
            this.sdkErrorStream.emit(err as MitekSdkError);
        }

        if (mode === 'MANUAL') {
            await delay(1000);
            this.displayStoppedStream.emit();
        } else {
            // In Auto mode, the mutation observer will be responsible for detecting when the Mitek UI is removed and emitting displayStopped
            await this.displayStopped$.toPromise();
            await delay(1000);
        }
        return;
    }
}

async function getServiceInstance() {
    return (await DiContainer.getInstance()).get(CheckCaptureService);
}
